import { getMyProfile, isCouponAvailable, syncSubscriptions } from '@/api';
import { ClientAuth, Device } from '@/classes';
import { SubscribeModalContent } from '@/features/subscribe';
import { AlertModalContent, ConfirmModalContent } from '@/shared/ui';
import { setFocus } from '@noriginmedia/norigin-spatial-navigation';
import { createContext, PropsWithChildren, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const PopupContext = createContext<{
  popupData: {
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
  };
  setPopupData: React.Dispatch<
    React.SetStateAction<{
      open: boolean;
      onClose: () => void;
      children: React.ReactNode;
    }>
  >;
  showSystemErrorPopup: (key?: string) => void;
  showSubscribePopup: (sourcePage: string, focusKey?: string, onClose?: () => void, promotionCode?: string) => void;
  showLoadVideoPopup: (props: { onConfirm: () => void; onCancel: () => void }) => void;
}>({
  popupData: {
    open: false,
    onClose: () => {},
    children: null,
  },
  setPopupData: () => {},
  showSystemErrorPopup: () => {},
  showSubscribePopup: () => {},
  showLoadVideoPopup: () => {},
});

export type PopupContextProviderProps = PropsWithChildren;

export const PopupContextProvider = ({ children }: PopupContextProviderProps) => {
  const { t } = useTranslation();
  const [popupData, setPopupData] = useState<{
    open: boolean;
    onClose: () => void;
    children: React.ReactNode;
  }>({
    open: false,
    onClose: () => {},
    children: null,
  });

  const showSystemErrorPopup = useCallback(
    (key: string = 'data_fail') => {
      setPopupData((prev) => {
        if (!prev.open) {
          const onClick = () => {
            setPopupData({ open: false, onClose: () => {}, children: null });
            Device.close();
          };

          return {
            open: true,
            onClose: onClick,
            children: <AlertModalContent message={t(key)} buttonMessage={t('button_ok')} onClick={onClick} />,
          };
        } else {
          return prev;
        }
      });
    },
    [t],
  );

  const showSubscribePopup = useCallback(
    async (sourcePage: string, focusKey?: string, onClose?: () => void, _promotionCode?: string) => {
      // ! Tizen Promotion 기간동안만 진행 (추후 전체 제거 MyProfile Event Card 도 제거 필요)
      let promotionCode = _promotionCode || (Device.isTizen ? 'SAMSUNG2025' : undefined);
      if (promotionCode) {
        try {
          const isAvailablePromotionCode = await isCouponAvailable(promotionCode);
          if (!isAvailablePromotionCode) {
            promotionCode = undefined;
          }
        } catch {
          promotionCode = undefined;
        }
      }

      setPopupData((prev) => {
        if (!prev.open) {
          const handleClose = async (scannedSubscriptionURL: boolean) => {
            if (scannedSubscriptionURL) {
              try {
                await syncSubscriptions();

                const user = await getMyProfile();
                ClientAuth.user = user;
              } catch (e) {
                if (process.env.NODE_ENV === 'development') {
                  console.error(e);
                }
              }
            }

            setPopupData({ open: false, onClose: () => {}, children: null });

            if (onClose) {
              onClose();
            } else if (focusKey) {
              setFocus(focusKey);
            }
          };

          return {
            open: true,
            onClose: () => handleClose(false),
            children: (
              <SubscribeModalContent sourcePage={sourcePage} onClose={handleClose} promotionCode={promotionCode} />
            ),
          };
        } else {
          return prev;
        }
      });
    },
    [],
  );

  const showLoadVideoPopup = useCallback(
    ({ onCancel, onConfirm }: { onCancel: () => void; onConfirm: () => void }) => {
      setPopupData((prev) => {
        if (!prev.open) {
          const handleConfirm = () => {
            setPopupData({ open: false, onClose: () => {}, children: null });
            onConfirm();
          };
          const handleCancel = () => {
            setPopupData({ open: false, onClose: () => {}, children: null });
            onCancel();
          };

          return {
            open: true,
            onClose: handleCancel,
            children: (
              <ConfirmModalContent
                message={t('confirm_continue_watching')}
                confirmMessage={t('button_continue_watching')}
                cancelMessage={t('button_from_beginning')}
                onConfirm={handleConfirm}
                onCancel={handleCancel}
              />
            ),
          };
        } else {
          return prev;
        }
      });
    },
    [t],
  );

  return (
    <PopupContext.Provider
      value={{ popupData, setPopupData, showSystemErrorPopup, showLoadVideoPopup, showSubscribePopup }}
    >
      {children}
    </PopupContext.Provider>
  );
};
